import { useFormSelectedSkillsFrameworks } from "PFApp/activities/activity_form/hooks/use_form_selected_skills_frameworks";
import { SkillsFrameworksSidePanel } from "PFApp/components/activity_form/form_selectors/skills_frameworks_selector";
import { FIELD_SKILLS_FRAMEWORK_IDS_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { Button } from "PFComponents/button";
import { Modal } from "PFComponents/modal";
import { Tooltip } from "PFComponents/tooltip";
import { SkillsFramework } from "PFCore/types";
import { Dispatch, SetStateAction, useState } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useActivityFormStore, useActivityFormStoreActions } from "../../../../activity_form_store";
import { useSubtemplate } from "../../../../hooks/use_subtemplate";

type SkillsFrameworksSectionActionsProps = {
  initiallyPreselectedSkillsFramework?: SkillsFramework;
  isConfigMatchFilterOn: boolean;
  isSkillsFrameworksSuggestionsFetching?: boolean;
  onFrameworksChange: (selectedFrameworks: SkillsFramework[]) => void;
  primarySkillsFrameworksSuggestions?: SkillsFramework[];
  secondarySkillsFrameworksSuggestions?: SkillsFramework[];
  setInitiallyPreselectedSkillsFramework: Dispatch<SetStateAction<SkillsFramework | undefined>>;
  setIsConfigMatchFilterOn: Dispatch<SetStateAction<boolean>>;
  setShowSidePanel: Dispatch<SetStateAction<boolean>>;
  showSidePanel: boolean;
};

export const SkillsFrameworksSectionActions = ({
  initiallyPreselectedSkillsFramework,
  isConfigMatchFilterOn,
  isSkillsFrameworksSuggestionsFetching,
  onFrameworksChange,
  primarySkillsFrameworksSuggestions,
  secondarySkillsFrameworksSuggestions,
  setInitiallyPreselectedSkillsFramework,
  setIsConfigMatchFilterOn,
  setShowSidePanel,
  showSidePanel
}: SkillsFrameworksSectionActionsProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.skillsFrameworks" });
  const template = useActivityFormStore((state) => state.template);
  const activityCreator = useActivityFormStore((state) => state.activityCreator);
  const { saveFrameworksValuesInCache } = useActivityFormStoreActions();
  const subtemplate = useSubtemplate();
  const { selectedSkillsFrameworks, isSkillsFrameworksLoading } = useFormSelectedSkillsFrameworks();
  const skillsFrameworksIds = useWatch({ name: FIELD_SKILLS_FRAMEWORK_IDS_KEY });

  const [showUnlinkModal, setShowUnlinkModal] = useState(false);

  const isAnyFrameworkAdded = skillsFrameworksIds.length > 0;
  const isActionDisabled = isAnyFrameworkAdded && isSkillsFrameworksLoading;

  const unlinkSkillsFrameworks = () => {
    onFrameworksChange([]);
    saveFrameworksValuesInCache([]);
  };

  const handleCloseSidePanel = () => {
    setShowSidePanel(false);
    setInitiallyPreselectedSkillsFramework(undefined);
  };

  return (
    <>
      {isAnyFrameworkAdded ? (
        <Button
          kind="text"
          text={t("remove")}
          title={t("removeSkillsFrameworks")}
          onClick={() => setShowUnlinkModal(true)}
          disabled={isActionDisabled}
        />
      ) : (
        <Tooltip
          content={t("selectSubtemplateToAddFramework", {
            template: t(`templates.${template.key}` as unknown as TemplateStringsArray)
          })}
        >
          <Button
            kind="text"
            text={t("add")}
            title={t("addSkillsFrameworks")}
            onClick={() => setShowSidePanel(true)}
            disabled={isActionDisabled}
          />
        </Tooltip>
      )}
      {showUnlinkModal && (
        <Modal
          title={t("removeSkillsFrameworks")}
          onOK={unlinkSkillsFrameworks}
          kindOK="danger"
          onClose={() => setShowUnlinkModal(false)}
        >
          {t("removeConfirmationMessage")}
        </Modal>
      )}
      {showSidePanel && (
        <SkillsFrameworksSidePanel
          isConfigMatchFilterOn={isConfigMatchFilterOn}
          setIsConfigMatchFilterOn={setIsConfigMatchFilterOn}
          isSkillsFrameworksSuggestionsFetching={isSkillsFrameworksSuggestionsFetching}
          template={template}
          subtemplate={subtemplate}
          activityCreator={activityCreator}
          selectedSkillsFrameworks={selectedSkillsFrameworks}
          primarySkillsFrameworksSuggestions={primarySkillsFrameworksSuggestions}
          secondarySkillsFrameworksSuggestions={secondarySkillsFrameworksSuggestions}
          onClose={handleCloseSidePanel}
          onFrameworksChange={onFrameworksChange}
          onPostPopulate={saveFrameworksValuesInCache}
          showSkillsFrameworksSuggestions
          showConfigMatchFilterToggle
          initiallyPreselectedSkillsFramework={initiallyPreselectedSkillsFramework}
        />
      )}
    </>
  );
};
